import { useEffect, useState } from "react";
import { DEFAULT_ROW_HEIGHT, HEADER_ROW_HEIGHT, EXTRA_SCROLL_ROWS } from "../constants";

// Excel's hard row ceiling (row 1,048,576).
const MAX_ROWS = 1048576;
// Start revealing more rows this far before the bottom edge so new rows
// are already there as the user scrolls into them.
const GROW_AHEAD_PX = 600;

/**
 * useRowGrowth
 * Vertical twin of useColumnGrowth: grows the number of rows the grid
 * knows about as it is scrolled DOWN, so the sheet behaves like Excel's
 * "endless" row grid instead of stopping at the parsed/padded row count.
 *
 * The grown count is fed to useRowWindow (the existing row virtualizer),
 * so even when the count is large only the on-screen slice is mounted —
 * the rows beyond the sheet's data simply render empty.
 *
 * Grow-only within a sheet; resets to the sheet's own row count on switch.
 */
export default function useRowGrowth({ gridWrapperRef, baseRows, rowHeights, activeSheet }) {
  const [displayRows, setDisplayRows] = useState(baseRows);

  // Reset to the sheet's own row count when the sheet (or its parsed row
  // count) changes.
  useEffect(() => { setDisplayRows(baseRows); }, [baseRows, activeSheet]);

  useEffect(() => {
    const el = gridWrapperRef.current;
    if (!el) return undefined;

    // Pixel height of the sheet's OWN rows (+ the column-header band).
    // Rows beyond `baseRows` are assumed to be the default height.
    let baseHeight = HEADER_ROW_HEIGHT;
    for (let i = 0; i < baseRows; i += 1) baseHeight += (rowHeights[i] || DEFAULT_ROW_HEIGHT);

    let raf = 0;
    const recompute = () => {
      raf = 0;
      const need = el.scrollTop + el.clientHeight + GROW_AHEAD_PX;
      let target = baseRows;
      if (need > baseHeight) {
        target = baseRows + Math.ceil((need - baseHeight) / DEFAULT_ROW_HEIGHT) + EXTRA_SCROLL_ROWS;
      }
      target = Math.min(MAX_ROWS, Math.max(baseRows, target));
      // Grow-only: never drop rows the user has already scrolled past.
      setDisplayRows((prev) => (target > prev ? target : prev));
    };

    const onScroll = () => { if (!raf) raf = requestAnimationFrame(recompute); };
    recompute(); // fill the viewport on first paint / sheet switch
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(recompute) : null;
    if (ro) ro.observe(el);

    return () => {
      el.removeEventListener("scroll", onScroll);
      if (ro) ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [gridWrapperRef, baseRows, rowHeights, activeSheet]);

  return displayRows;
}
